import {
  setPaymentError,
  setPaymentIsProcessing,
  setPaymentMethodsSuccess,
  setShowAddPaymentForm,
  setPaymentMethodsLoading,
  setPaymentMethodsError,
  setSavedPaymentMethods,
  setSaveStripePaymentMethodLoading,
  setSelectedPaymentMethod,
} from "../../../store/Store";
import { CardElement } from "@stripe/react-stripe-js";
import i18n from "i18next";
import ApiWrapper from "../../general/ApiWrapper";
import { cloud9Funding } from "../../general/AppHelpers";

const { t } = i18n;

export const saveCloud9PaymentMethod = async ({
  token,
  tokenData,
  ticketData,
  accessToken,
}) => {
  // Handle saving card without processing payment

  setPaymentIsProcessing(true);
  setPaymentError(null);

  if (!ticketData?.resource?.my_client_client_id || !token || !tokenData) {
    throw new Error(t("payment_methods.missing_required_data"));
  }

  // Extract card details from tokenData
  const cardData = {
    company_id: ticketData.resource.company.id,
    location_id: ticketData.resource.location.id,
    token: token,
    token_data: {
      funding: (
        cloud9Funding(tokenData?.CardDebitCreditIndicator) || ""
      ).toLowerCase(),
      brand: tokenData.Brand,
      last4: (tokenData.AccountNum || "").slice(-4),
      expDate: tokenData.ExpDate,
      month: (tokenData.ExpDate || "").substring(0, 2),
      year: "20" + (tokenData.ExpDate || "").substring(2),
    },
    token2: token,
    address_country: "US",
    add_to_client: true,
  };

  // Save the payment method
  const { error } = await ApiWrapper.post(
    "/api/mkp/v1/intake/payment_methods",
    cardData,
    { accessToken }
  );

  if (error) {
    setPaymentError({
      message: error.message || t("payment_methods.failed_to_save"),
    });
    setPaymentIsProcessing(false);
    return;
  }

  setPaymentMethodsSuccess(t("payment_methods.added_successfully"));
  setShowAddPaymentForm(false);
  setPaymentIsProcessing(false);
};

export const saveStripePaymentMethod = async ({
  elements,
  accessToken,
  stripe,
}) => {
  setSaveStripePaymentMethodLoading(true);
  setPaymentMethodsError(null);

  const { data: setupData, error } = await ApiWrapper.post(
    "/api/mkp/v1/intake/payment_methods/session",
    { payment_type: "card" },
    { accessToken }
  );

  // Use Stripe to confirm card setup
  const cardElement = elements.getElement(CardElement);

  if (!setupData || !setupData?.client_secret) {
    setPaymentMethodsError(t("payment_methods.failed_to_save"));
    setSaveStripePaymentMethodLoading(false);
    cardElement.clear();
    return;
  }

  const { client_secret } = setupData;

  const { error: stripeError } = await stripe.confirmCardSetup(client_secret, {
    payment_method: {
      card: cardElement,
    },
  });

  if (stripeError) {
    throw stripeError;
  }

  if (error) {
    setPaymentMethodsError(
      error.message || t("payment_methods.failed_to_save")
    );
    setSaveStripePaymentMethodLoading(false);
    return;
  }
  setTimeout(async () => {
    await fetchPaymentMethods({ accessToken });

    setPaymentMethodsSuccess(t("payment_methods.added_successfully"));
    setShowAddPaymentForm(false);
    setSaveStripePaymentMethodLoading(false);
  }, 1000);
};

export const fetchPaymentMethods = async ({ accessToken }) => {
  const response = await ApiWrapper.get("/api/mkp/v1/intake/payment_methods", {
    accessToken,
  });

  if (response?.error) {
    setPaymentMethodsError(
      response.error.message || t("payment_methods.failed_to_load")
    );
    setPaymentMethodsLoading(false);
    return;
  }

  // Ensure we're handling the response data correctly
  // The API might return an object with cards array, payment_methods property,
  // or directly an array of payment methods
  let methods =
    response?.data?.cards || response?.data?.payment_methods || response?.data;
  methods = Array.isArray(methods) ? methods : [];
  setSavedPaymentMethods(methods);
  setSelectedPaymentMethod(methods?.[0]);
  setPaymentMethodsLoading(false);
};

export const deletePaymentMethod = async ({ id, accessToken, ticketData }) => {
  const response = await ApiWrapper.delete(
    `/api/mkp/v1/intake/payment_methods/${id}`,
    { accessToken }
  );

  if (response?.error) {
    setPaymentMethodsLoading(false);
    setPaymentMethodsError(
      response.error.message || t("payment_methods.delete_fail")
    );
    return;
  }

  setPaymentMethodsSuccess(t("payment_methods.delete_success"));
  fetchPaymentMethods({ accessToken });
};
